
import { Component, Vue } from "vue-property-decorator";
import { DatabaseBackupFile, DatabaseBackupJob, DatabaseRestoreValidation, DBInfo } from "@/survey";

@Component({})
export default class DatabaseBackupRestore extends Vue {
  client = Vue.prototype.$surveyClient;
  backupJob: DatabaseBackupJob | null = null;
  restoreJob: DatabaseBackupJob | null = null;
  backupFiles: DatabaseBackupFile[] = [];
  validation: DatabaseRestoreValidation | null = null;
  dbinfo: DBInfo | null = null;
  restoreFile: File | null = null;
  restoreConfirmation = "";
  backupTimer: number | null = null;
  restoreTimer: number | null = null;
  restoreFileSelectTimer: number | null = null;
  creatingBackup = false;
  selectingRestoreFile = false;
  validatingRestore = false;
  loadingBackupFiles = false;
  downloadingBackup = false;
  downloadingFileName = "";
  deletingFileName = "";
  backupError = "";
  backupFilesError = "";
  restoreError = "";

  get backupBusy() {
    return this.backupJob !== null && (this.backupJob.status === "queued" || this.backupJob.status === "running");
  }

  get restoreBusy() {
    return this.restoreJob !== null && (this.restoreJob.status === "queued" || this.restoreJob.status === "running");
  }

  get restoreConfirmed() {
    return this.validation !== null && this.restoreConfirmation === this.validation.currentDatabaseName;
  }

  get databaseSizeMb() {
    if (this.dbinfo === null) {
      return 0;
    }
    return this.dbinfo.table_info.reduce((sum, item) => sum + item.t3, 0);
  }

  get databaseRows() {
    if (this.dbinfo === null) {
      return 0;
    }
    return this.dbinfo.table_info.reduce((sum, item) => sum + item.t4, 0);
  }

  get backupEstimateText() {
    if (this.dbinfo === null) {
      return "Loading database size estimate...";
    }
    return `Current database is about ${this.formatBytes(this.databaseSizeMb * 1024 * 1024)} across ${this.databaseRows.toLocaleString()} rows. Estimated backup time: ${this.estimatedDurationText}. Download size is usually smaller than raw database size because the package is compressed.`;
  }

  get backupEstimateShortText() {
    return `Rough estimate: ${this.estimatedDurationText}`;
  }

  get estimatedDurationText() {
    const size = this.databaseSizeMb;
    if (size <= 0) {
      return "a few minutes";
    }
    const minutes = Math.max(1, Math.ceil(size / 1024 * 2));
    if (minutes < 2) {
      return "about 1 minute";
    }
    if (minutes < 60) {
      return `about ${minutes} minutes`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes === 0 ? `about ${hours} hours` : `about ${hours} hours ${remainingMinutes} minutes`;
  }

  get jobFields() {
    return [
      { key: "status", label: "Status" },
      { key: "message", label: "Message" },
      { key: "fileName", label: "File" },
      { key: "size", label: "Size", formatter: this.formatBytes },
      { key: "createdAt", label: "Created", formatter: this.formatDate },
      { key: "completedAt", label: "Completed", formatter: this.formatDate }
    ];
  }

  get manifestFields() {
    return [
      { key: "databaseType", label: "Database Type" },
      { key: "databaseName", label: "Database" },
      { key: "schemaName", label: "Schema" },
      { key: "appVersion", label: "App Version" },
      { key: "createdAt", label: "Created", formatter: this.formatDate },
      { key: "schemaFingerprint", label: "Schema Fingerprint" }
    ];
  }

  get backupFileFields() {
    return [
      { key: "fileName", label: "File" },
      { key: "size", label: "Size", formatter: this.formatBytes },
      { key: "createdAt", label: "Created", formatter: this.formatDate },
      { key: "actions", label: "Actions" }
    ];
  }

  beforeDestroy() {
    this.clearTimer(this.backupTimer);
    this.clearTimer(this.restoreTimer);
    this.clearTimer(this.restoreFileSelectTimer);
  }

  async created() {
    await this.loadDatabaseInfo();
    await this.loadBackupFiles();
  }

  async loadDatabaseInfo() {
    try {
      this.dbinfo = await this.client.getDbInfo() as any;
    } catch (e) {
      this.dbinfo = null;
    }
  }

  async createBackup() {
    if (!confirm(`Create a database backup now? ${this.backupEstimateShortText}.`)) {
      return;
    }
    this.creatingBackup = true;
    this.backupError = "";
    this.backupJob = null;
    try {
      this.backupJob = await this.client.startDatabaseBackup();
      this.pollBackup();
    } catch (e) {
      this.backupError = `Error starting backup: ${e}`;
    } finally {
      this.creatingBackup = false;
    }
  }

  async pollBackup() {
    if (this.backupJob === null) {
      return;
    }
    const backupJob = await this.client.getDatabaseJob(this.backupJob.id);
    this.backupJob = backupJob;
    if (this.backupBusy) {
      this.backupTimer = window.setTimeout(() => this.pollBackup(), 3000);
    } else if (backupJob.status === "complete") {
      await this.loadBackupFiles();
    }
  }

  async downloadBackup() {
    if (this.backupJob === null) {
      return;
    }
    this.backupError = "";
    this.downloadingBackup = true;
    try {
      const blob = await this.client.downloadDatabaseBackup(this.backupJob.id);
      this.saveBlob(blob, this.backupJob.fileName || "pinpoint-backup.zip");
    } catch (e) {
      this.backupError = `Error downloading backup: ${e}`;
    } finally {
      this.downloadingBackup = false;
    }
  }

  async loadBackupFiles() {
    this.loadingBackupFiles = true;
    this.backupFilesError = "";
    try {
      this.backupFiles = await this.client.listDatabaseBackupFiles();
    } catch (e) {
      this.backupFilesError = `Error loading backup files: ${e}`;
    } finally {
      this.loadingBackupFiles = false;
    }
  }

  async downloadBackupFile(fileName: string) {
    this.backupFilesError = "";
    this.downloadingFileName = fileName;
    try {
      const blob = await this.client.downloadDatabaseBackupFile(fileName);
      this.saveBlob(blob, fileName);
    } catch (e) {
      this.backupFilesError = `Error downloading backup file: ${e}`;
    } finally {
      this.downloadingFileName = "";
    }
  }

  async deleteBackupFile(fileName: string) {
    if (!confirm(`Delete backup file ${fileName}? This cannot be undone.`)) {
      return;
    }
    this.backupFilesError = "";
    this.deletingFileName = fileName;
    try {
      await this.client.deleteDatabaseBackupFile(fileName);
      await this.loadBackupFiles();
    } catch (e) {
      this.backupFilesError = `Error deleting backup file: ${e}`;
    } finally {
      this.deletingFileName = "";
    }
  }

  saveBlob(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  async validateRestore() {
    if (this.restoreFile === null) {
      return;
    }
    this.restoreError = "";
    this.validatingRestore = true;
    try {
      this.validation = await this.client.validateDatabaseRestore(this.restoreFile);
      this.restoreConfirmation = "";
      this.restoreJob = null;
    } catch (e) {
      this.restoreError = `Error validating restore package: ${e}`;
    } finally {
      this.validatingRestore = false;
    }
  }

  onRestoreFileSelected() {
    this.validation = null;
    this.restoreConfirmation = "";
    this.restoreJob = null;
    this.restoreError = "";
    this.selectingRestoreFile = true;
    this.clearTimer(this.restoreFileSelectTimer);
    this.restoreFileSelectTimer = window.setTimeout(() => {
      this.selectingRestoreFile = false;
      this.restoreFileSelectTimer = null;
    }, 500);
  }

  async restoreBackup() {
    if (this.restoreFile === null || !this.restoreConfirmed) {
      return;
    }
    const databaseName = this.validation ? this.validation.currentDatabaseName : "the current database";
    const warningText = this.validation && this.validation.warnings && this.validation.warnings.length
      ? `\n\nWarnings:\n${this.validation.warnings.join("\n")}`
      : "";
    if (!confirm(`Restore this backup to ${databaseName}? This will replace the current database contents after creating a safety backup.${warningText}`)) {
      return;
    }
    this.restoreError = "";
    try {
      this.restoreJob = await this.client.startDatabaseRestore(this.restoreFile, this.restoreConfirmation);
      this.pollRestore();
    } catch (e) {
      this.restoreError = `Error starting restore: ${e}`;
    }
  }

  async pollRestore() {
    if (this.restoreJob === null) {
      return;
    }
    const currentJob = this.restoreJob;
    try {
      this.restoreJob = await this.client.getDatabaseJob(currentJob.id);
      if (this.restoreBusy) {
        this.restoreTimer = window.setTimeout(() => this.pollRestore(), 3000);
      }
    } catch (e) {
      this.restoreJob = {
        ...currentJob,
        status: "failed",
        message: "Restore status is no longer available. Refresh the database info and verify the restored data before starting another restore."
      };
      this.restoreError = `Error checking restore status: ${e}`;
    }
  }

  clearTimer(timer: number | null) {
    if (timer !== null) {
      window.clearTimeout(timer);
    }
  }

  formatBytes(value: number | null) {
    if (value === null || value === undefined) {
      return "";
    }
    if (value < 1024) {
      return `${value} B`;
    }
    if (value < 1024 * 1024) {
      return `${(value / 1024).toFixed(1)} KB`;
    }
    if (value < 1024 * 1024 * 1024) {
      return `${(value / 1024 / 1024).toFixed(1)} MB`;
    }
    return `${(value / 1024 / 1024 / 1024).toFixed(1)} GB`;
  }

  formatDate(value: string | null) {
    if (!value) {
      return "";
    }
    return new Date(value).toLocaleString();
  }
}
